import { SentryFeatureTags, logException } from "@punchup/sentry";

import { Database } from "@punchup/data/supabase.types";
import { SupabaseClient } from "@supabase/supabase-js";

export type UserExtraRow = Database["public"]["Tables"]["users_extra"]["Row"];
export type UserExtraUpdate =
  Database["public"]["Tables"]["users_extra"]["Update"];

export async function getUserLastActiveAt(
  supabase: SupabaseClient<Database>
): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.user) {
    throw new Error(
      "Supabase client must have an active session to get last activity"
    );
  }

  const { data, error } = await supabase
    .from("users_extra")
    .select(`last_active_at`)
    .eq("id", session.user.id)
    .maybeSingle();

  if (data) {
    return data.last_active_at;
  }

  logException(new Error("Issue getting last activity for user with id"), {
    extra: {
      userId: session.user.id,
      error: error?.message,
    },
    tags: {
      feature: SentryFeatureTags.Metrics,
      supabase: true,
      userId: session.user.id,
      error: error?.message || "Unknown error",
    },
  });
  return null;
}

export async function updateUserLastActiveAt(
  supabase: SupabaseClient<Database>,
  last_active_at: UserExtraUpdate["last_active_at"]
) {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.user) {
    throw new Error(
      "Supabase client must have an active session to update last activity"
    );
  }

  const { count, error } = await supabase
    .from("users_extra")
    .update(
      {
        last_active_at,
      },
      {
        count: "exact",
      }
    )
    .eq("id", session.user.id);

  if (error) {
    logException(error, {
      data: `Issue updating last activity for user with id: ${session.user.id}`,
    });
  } else {
    if (count === 1) {
      return true;
    } else {
      logException(
        `Issue updating last activity for user with id: ${session.user.id}. Row count was ${count}`
      );
    }
  }
}

export async function getUserIDFromUnsubToken(
  supabaseAdmin: SupabaseClient,
  unsubToken: string
): Promise<string | null> {
  const { data, error } = await supabaseAdmin
    .from("temp_auth_tokens")
    .select("user_id")
    .eq("access_token", unsubToken)
    .maybeSingle();
  if (error) {
    logException(error, {
      extra: {
        data: `Issue getting user id from unsub token`,
        unsubToken,
      },
      tags: {
        supabase: true,
        feature: SentryFeatureTags.Email,
      },
    });
    return null;
  }
  if (data) {
    return data.user_id;
  }
  return null;
}
